import { useRef, useEffect, useState } from 'react'
import { XMarkIcon, PrinterIcon, BanknotesIcon } from '@heroicons/react/24/outline'
import { settingService } from '../../services/settingService'
import { MpesaPaymentModal } from '../payments/MpesaPaymentModal'
import toast from 'react-hot-toast'

interface ReceiptModalProps {
  isOpen: boolean
  onClose: () => void
  sale: any
  onPaymentComplete?: () => void
}

interface CompanySettings {
  name: string
  address: string
  phone: string
  email: string
  taxNumber: string
  logo: string
  receiptFooter: string
}

export const ReceiptModal = ({ isOpen, onClose, sale, onPaymentComplete }: ReceiptModalProps) => {
  const printRef = useRef<HTMLDivElement>(null)
  const [companySettings, setCompanySettings] = useState<CompanySettings>({
    name: 'Aperture Camera Store',
    address: 'Nairobi, Kenya',
    phone: '+254 700 000 000',
    email: '',
    taxNumber: '',
    logo: '/logo.png',
    receiptFooter: 'Thank you for your business!',
  })
  const [isLoading, setIsLoading] = useState(false)
  const [showPaymentModal, setShowPaymentModal] = useState(false)
  const [showMpesaModal, setShowMpesaModal] = useState(false)
  const [selectedMethod, setSelectedMethod] = useState<string | null>(null)
  const [reference, setReference] = useState('')
  const [isProcessing, setIsProcessing] = useState(false)

  useEffect(() => {
    if (isOpen) {
      fetchCompanySettings()
    }
  }, [isOpen])

  const fetchCompanySettings = async () => {
    setIsLoading(true)
    try {
      const response = await settingService.getCompanySettings()
      if (response.data?.data) {
        setCompanySettings(response.data.data)
      }
    } catch (error) {
      toast.error('Failed to load company settings')
    } finally {
      setIsLoading(false)
    }
  }

  if (!isOpen || !sale) return null

  const balance = Number(sale.balance_due || 0)
  const hasBalance = balance > 0

  const handlePrint = () => {
    const printContent = printRef.current
    if (!printContent) return

    const printWindow = window.open('', '_blank')
    if (!printWindow) {
      alert('Please allow popups to print receipts')
      return
    }

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Receipt - ${sale.sale_number}</title>
          <style>
            @page { size: 80mm auto; margin: 0; }
            body { 
              font-family: 'Courier New', monospace; 
              font-size: 12px; 
              line-height: 1.4;
              margin: 0;
              padding: 10px;
              width: 80mm;
            }
            .receipt { max-width: 80mm; }
            .header { text-align: center; margin-bottom: 15px; }
            .logo { max-width: 60px; height: auto; margin-bottom: 5px; }
            .company-name { font-size: 16px; font-weight: bold; }
            .company-info { font-size: 10px; color: #666; }
            .divider { border-top: 1px dashed #000; margin: 10px 0; }
            .section { margin: 10px 0; }
            .row { display: flex; justify-content: space-between; }
            .item { margin: 5px 0; }
            .item-name { font-weight: bold; }
            .totals { margin-top: 15px; }
            .total-row { display: flex; justify-content: space-between; font-weight: bold; }
            .grand-total { font-size: 14px; border-top: 1px solid #000; padding-top: 5px; margin-top: 5px; }
            .payment-info { margin-top: 15px; padding-top: 10px; border-top: 1px dashed #000; }
            .footer { text-align: center; margin-top: 20px; font-size: 10px; }
            .thank-you { font-size: 14px; font-weight: bold; margin: 10px 0; }
            @media print {
              body { width: 80mm; }
              .no-print { display: none; }
            }
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
          <script>
            window.onload = function() {
              setTimeout(function() {
                window.print();
                window.close();
              }, 200);
            };
          </script>
        </body>
      </html>
    `)
    printWindow.document.close()
  }

  const handleReceivePayment = () => {
    setShowPaymentModal(true)
  }

  const handleMethodSelect = (method: string) => {
    setSelectedMethod(method)
    if (method === 'mpesa') {
      setShowMpesaModal(true)
    }
  }

  const handleMpesaComplete = () => {
    setShowMpesaModal(false)
    setShowPaymentModal(false)
    if (onPaymentComplete) {
      onPaymentComplete()
    }
  }

  const handleCashPayment = async () => {
    const { saleService } = await import('../../services/saleService')
    setIsProcessing(true)
    try {
      await saleService.addPayment(sale.sale_id, {
        amount: balance,
        payment_method: selectedMethod,
        reference_number: reference || undefined,
      })
      toast.success('Payment received successfully')
      setShowPaymentModal(false)
      setSelectedMethod(null)
      setReference('')
      if (onPaymentComplete) {
        onPaymentComplete()
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Failed to process payment')
    } finally {
      setIsProcessing(false)
    }
  }

  const customerName = sale.customers 
    ? (sale.customers.company_name || `${sale.customers.first_name || ''} ${sale.customers.last_name || ''}`.trim())
    : 'Walk-in Customer'

  const subtotal = Number(sale.subtotal || 0)
  const discount = Number(sale.discount_amount || 0)
  const taxAmount = Number(sale.tax_amount || 0)
  const vatType = sale.vat_type as string | undefined
  const taxPercent = Number(sale.tax_percent ?? 0)
  const total = Number(sale.total_amount || 0)
  const paid = Number(sale.paid_amount || 0)

  const vatLabel = vatType === 'vat_exclusive'
    ? `VAT Excl. (${taxPercent}%)`
    : vatType === 'vat_inclusive'
    ? `VAT Incl. (${taxPercent}%)`
    : null

  const formatDate = (date: string) => {
    return new Date(date).toLocaleString('en-KE', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const formatCurrency = (amount: number) => {
    return `KSh ${amount.toLocaleString('en-KE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  }

  // Payment method selection modal
  if (showPaymentModal && !showMpesaModal && !selectedMethod) {
    return (
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen px-4 py-6">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowPaymentModal(false)} />
          <div className="relative bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold mb-4">Receive Payment</h3>
            <p className="text-sm text-gray-600 mb-4">Amount: {formatCurrency(balance)}</p>
            <div className="grid grid-cols-2 gap-4">
              <button
                onClick={() => handleMethodSelect('cash')}
                className="p-4 border-2 border-gray-200 rounded-lg hover:border-green-500 hover:bg-green-50 flex flex-col items-center gap-2"
              >
                <BanknotesIcon className="w-8 h-8 text-green-600" />
                <span className="font-medium">Cash</span>
              </button>
              <button
                onClick={() => handleMethodSelect('mpesa')}
                className="p-4 border-2 border-gray-200 rounded-lg hover:border-green-500 hover:bg-green-50 flex flex-col items-center gap-2"
              >
                <svg className="w-8 h-8 text-green-600" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17 1.01L7 1c-1.1 0-2 .9-2 2v18c0 1.1.9 2 2 2h10c1.1 0 2-.9 2-2V3c0-1.1-.9-1.99-2-1.99zM17 19H7V5h10v14z"/>
                </svg>
                <span className="font-medium">M-Pesa</span>
              </button>
            </div>
            <button
              onClick={() => setShowPaymentModal(false)}
              className="w-full mt-4 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </div>
        <MpesaPaymentModal
          isOpen={showMpesaModal}
          onClose={() => setShowMpesaModal(false)}
          amount={balance}
          saleId={sale.sale_id}
          customerId={sale.customer_id}
          onPaymentComplete={handleMpesaComplete}
        />
      </div>
    )
  }

  // Cash payment confirmation
  if (showPaymentModal && selectedMethod && selectedMethod !== 'mpesa') {
    return (
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen px-4 py-6">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowPaymentModal(false)} />
          <div className="relative bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold mb-4">Confirm {selectedMethod.charAt(0).toUpperCase() + selectedMethod.slice(1)} Payment</h3>
            <p className="text-sm text-gray-600 mb-4">Amount: {formatCurrency(balance)}</p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Reference (Optional)</label>
              <input
                type="text"
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                placeholder="Enter reference number..."
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setSelectedMethod(null)}
                disabled={isProcessing}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Back
              </button>
              <button
                onClick={handleCashPayment}
                disabled={isProcessing}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                {isProcessing ? 'Processing...' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 py-6">
        <div className="fixed inset-0 bg-black/50" onClick={onClose} />
        
        <div className="relative bg-white rounded-xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">Receipt Preview</h2>
            <div className="flex items-center gap-2">
              <button
                onClick={handlePrint}
                disabled={isLoading}
                className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
              >
                <PrinterIcon className="w-4 h-4" />
                Print
              </button>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Receipt Content */}
          <div className="p-6 overflow-auto max-h-[calc(90vh-160px)] bg-gray-50">
            <div 
              ref={printRef}
              className="bg-white p-6 shadow-sm"
              style={{ fontFamily: "'Courier New', monospace", fontSize: '12px', lineHeight: '1.4' }}
            >
              {/* Header */}
              <div style={{ textAlign: 'center', marginBottom: '15px' }}>
                <img 
                  src={companySettings.logo} 
                  alt="Logo" 
                  style={{ maxWidth: '80px', height: 'auto', marginBottom: '8px' }}
                  onError={(e) => { e.currentTarget.style.display = 'none' }}
                />
                <div style={{ fontSize: '16px', fontWeight: 'bold' }}>{companySettings.name}</div>
                <div style={{ fontSize: '10px', color: '#666' }}>
                  {companySettings.address}<br />
                  {companySettings.phone && `Tel: ${companySettings.phone}`}
                  {companySettings.email && <><br />{companySettings.email}</>}
                  {companySettings.taxNumber && <><br />PIN: {companySettings.taxNumber}</>}
                </div>
              </div>

              <div style={{ borderTop: '1px dashed #000', margin: '10px 0' }} />

              {/* Sale Info */}
              <div style={{ margin: '10px 0' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Receipt #:</span>
                  <span>{sale.sale_number}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Date:</span>
                  <span>{formatDate(sale.sale_date)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Customer:</span>
                  <span>{customerName}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Type:</span>
                  <span style={{ textTransform: 'capitalize' }}>{sale.sale_type}</span>
                </div>
              </div>

              <div style={{ borderTop: '1px dashed #000', margin: '10px 0' }} />

              {/* Items */}
              <div style={{ margin: '10px 0' }}>
                <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>ITEMS:</div>
                {sale.sale_items?.map((item: any, idx: number) => (
                  <div key={idx} style={{ margin: '5px 0' }}>
                    <div style={{ fontWeight: 'bold' }}>
                      {item.products?.product_name || 'Unknown Item'}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', paddingLeft: '10px' }}>
                      <span>{item.quantity} x {formatCurrency(Number(item.unit_price))}</span>
                      <span>{formatCurrency(Number(item.line_total || item.quantity * item.unit_price))}</span>
                    </div>
                    {item.discount_amount > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', paddingLeft: '10px', color: '#666' }}>
                        <span>Discount:</span>
                        <span>-{formatCurrency(Number(item.discount_amount))}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div style={{ borderTop: '1px dashed #000', margin: '10px 0' }} />

              {/* Totals */}
              <div style={{ marginTop: '15px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Subtotal:</span>
                  <span>{formatCurrency(subtotal)}</span>
                </div>
                {discount > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>Discount:</span>
                    <span>-{formatCurrency(discount)}</span>
                  </div>
                )}
                {vatLabel && taxAmount > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>{vatLabel}:</span>
                    <span>{formatCurrency(taxAmount)}</span>
                  </div>
                )}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  borderTop: '1px solid #000',
                  paddingTop: '5px',
                  marginTop: '5px'
                }}>
                  <span>TOTAL:</span>
                  <span>{formatCurrency(total)}</span>
                </div>
              </div>

              {/* Payment Info */}
              <div style={{ marginTop: '15px', paddingTop: '10px', borderTop: '1px dashed #000' }}>
                <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>PAYMENT:</div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>Amount Paid:</span>
                  <span>{formatCurrency(paid)}</span>
                </div>
                {balance > 0 ? (
                  <div style={{ display: 'flex', justifyContent: 'space-between', color: '#c00', fontWeight: 'bold' }}>
                    <span>Balance Due:</span>
                    <span>{formatCurrency(balance)}</span>
                  </div>
                ) : (
                  <div style={{ display: 'flex', justifyContent: 'space-between', color: '#0c0' }}>
                    <span>Status:</span>
                    <span>PAID IN FULL</span>
                  </div>
                )}
                
                {sale.payments?.length > 0 && (
                  <div style={{ marginTop: '10px' }}>
                    <div style={{ fontSize: '10px', color: '#666' }}>Payment Methods:</div>
                    {sale.payments.map((payment: any, idx: number) => (
                      <div key={idx} style={{ fontSize: '10px', display: 'flex', justifyContent: 'space-between' }}>
                        <span>{payment.payment_methods?.method_name || 'Payment'}:</span>
                        <span>{formatCurrency(Number(payment.amount))}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div style={{ textAlign: 'center', marginTop: '20px', fontSize: '10px' }}>
                <div style={{ fontSize: '14px', fontWeight: 'bold', margin: '10px 0' }}>
                  {companySettings.receiptFooter}
                </div>
                <div>Goods once sold cannot be returned.</div>
                <div>Keep this receipt for warranty claims.</div>
                <div style={{ marginTop: '10px' }}>
                  --- END OF RECEIPT ---
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="px-6 py-4 border-t bg-gray-50">
            {hasBalance ? (
              <div className="flex gap-3">
                <button
                  onClick={onClose}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100"
                >
                  Print & Close
                </button>
                <button
                  onClick={handleReceivePayment}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center justify-center gap-2"
                >
                  <BanknotesIcon className="w-4 h-4" />
                  Receive Payment
                </button>
              </div>
            ) : (
              <button
                onClick={onClose}
                className="w-full px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
              >
                Done
              </button>
            )}
          </div>
        </div>
      </div>
      <MpesaPaymentModal
        isOpen={showMpesaModal}
        onClose={() => setShowMpesaModal(false)}
        amount={balance}
        saleId={sale.sale_id}
        customerId={sale.customer_id}
        onPaymentComplete={handleMpesaComplete}
      />
    </div>
  )
}
